import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRelationships,
  deleteRelationship,
} from "../redux/actions/relationshipActions";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { CustomPaper } from "../styles/customPaper";
import Avatar from "@mui/material/Avatar";
import {
  Breadcrumbs,
  Button,
  Typography,
  Stack,
  Link,
  Container,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  TableContainer,
} from "@mui/material";
import {
  InputAdornment,
  Box,
  Autocomplete,
  TableSortLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AddIcon from "@mui/icons-material/Add";

import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { smallTitle } from "../styles/typography";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"; // User icon
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"; // Office icon

// Static gender translations - defined outside component to prevent re-renders
const GENDER_TRANSLATIONS = {
  'Male': {
    en: 'Male',
    nl: 'Man'
  },
  'Female': {
    en: 'Female',
    nl: 'Vrouw'
  },
  'Unknown': {
    en: 'Unknown',
    nl: 'Onbekend'
  }
};

// Breadcrumbs will be set in component with translation
const tableStyles = windowsTableStyles();

// Options will be set in component with translation
const tableHeaderStyles = {
  backgroundColor: "#f5f5f5", // Light grey background
  color: "#333", // Dark text color
  fontWeight: "bold", // Bold text
  // textTransform: 'uppercase', // Uppercase text
  letterSpacing: "0.1em", // Slightly increased letter spacing
  padding: "10px 15px", // Custom padding
};

const RelationshipPage = () => {
  const { t, i18n } = useTranslation(['pages', 'common', 'buttons', 'forms']);

  // Safe gender translation function - uses static translations
  const translateGender = (gender) => {
    const currentLang = i18n.language || 'en';
    const genderTranslation = GENDER_TRANSLATIONS[gender];
    if (genderTranslation && genderTranslation[currentLang]) {
      return genderTranslation[currentLang];
    }
    return gender; // fallback to original gender
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { relationships, loading, error } = useSelector(
    (state) => state.relationshipList
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [contractFilter, setContractFilter] = useState("");
  const [order, setOrder] = useState("asc");

  const breadcrumbs = [{ label: t('navigation:home'), href: "/" }];
  const typeOptions = [
    { value: "Client", label: t('pages:relationships.types.client') },
    { value: "Lawyer", label: t('pages:relationships.types.lawyer') },
    { value: "Counter Party", label: t('pages:relationships.types.counter_party') }
  ];
  const statusOptions = [
    { value: "Active", label: t('common:active') },
    { value: "Inactive", label: t('common:inactive') }
  ];
  const contractOptions = [
    { value: "Individual", label: t('pages:relationships.contract_types.individual') },
    { value: "Company", label: t('pages:relationships.contract_types.company') }
  ];
  const [orderBy, setOrderBy] = useState("");

  // Helper function to translate relationship types
  const getTranslatedRelationshipType = (type) => {
    switch (type) {
      case 'Client':
        return t('pages:relationships.types.client');
      case 'Lawyer':
        return t('pages:relationships.types.lawyer');
      case 'Counter Party':
        return t('pages:relationships.types.counter_party');
      default:
        return type;
    }
  };

  useEffect(() => {
    // Only fetch if not already loaded to avoid unnecessary API calls
    if (!relationships || relationships.length === 0) {
      dispatch(fetchRelationships());
    }
  }, [dispatch, relationships]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateRelationship = () => {
    navigate("/relationships/create");
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const buttonStyles = {
    borderRadius: 1, // Rounded corners
    padding: "6px 16px", // Custom padding
    textTransform: "none", // Disable uppercase text
    boxShadow: "none", // Remove default shadow
    height: "fit-content", // Fit the button height to its content
    "&:hover": {
      boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
    },
  };

  console.log("relationships are", relationships);
  // Filtered data based on search input
  const filteredRelationships = relationships?.filter((relationship) => {
    const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase();
    const searchText = normalizeString(search);

    const matchesSearch =
      ((relationship.firstName ||
        relationship.middleName ||
        relationship.lastName) &&
        normalizeString(
          `${relationship.firstName || ""} ${relationship.middleName || ""} ${relationship.lastName || ""
          }`
        ).includes(searchText)) ||
      (relationship.businessName &&
        normalizeString(relationship.businessName).includes(searchText)) ||
      (relationship.type &&
        normalizeString(relationship.type).includes(searchText)) ||
      (relationship.status &&
        normalizeString(relationship.status).includes(searchText)) ||
      (relationship.uniqueNumber &&
        normalizeString(relationship.uniqueNumber.toString()).includes(
          searchText
        )) ||
      (relationship.contacts &&
        relationship.contacts.some(
          (contact) =>
            (contact.phone &&
              normalizeString(contact.phone).includes(searchText)) ||
            (contact.email &&
              normalizeString(contact.email).includes(searchText))
        ));

    const matchesTypeFilter = typeFilter
      ? relationship.type === typeFilter
      : true;
    const matchesStatusFilter = statusFilter
      ? relationship.status === statusFilter
      : true;
    const matchesContractFilter = contractFilter
      ? relationship.contractType === contractFilter
      : true;
    return (
      matchesSearch &&
      matchesTypeFilter &&
      matchesStatusFilter &&
      matchesContractFilter
    );
  });
  const sortedRelationships = filteredRelationships?.sort((a, b) => {
    if (orderBy) {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "name") {
        aValue =
          a.contractType === "Individual"
            ? `${a.firstName || ""} ${a.middleName || ""} ${a.lastName || ""
              }`.trim()
            : a.businessName || "";
        bValue =
          b.contractType === "Individual"
            ? `${b.firstName || ""} ${b.middleName || ""} ${b.lastName || ""
              }`.trim()
            : b.businessName || "";
      } else if (orderBy === "phone" || orderBy === "email") {
        aValue =
          a.contacts && a.contacts.length > 0 ? a.contacts[0][orderBy] : "";
        bValue =
          b.contacts && b.contacts.length > 0 ? b.contacts[0][orderBy] : "";
      }

      if (order === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    }
    return 0;
  });


  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  // if (loading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:relationships.title')} breadcrumbs={breadcrumbs} />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateRelationship}
          size="small" // Decrease the size of the button
          sx={actionButtonStyles()}
        >
          {t('pages:relationships.create_new')}
        </Button>
      </Box>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label={t('common:searchAnything')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  // further adjustments to inner padding
                  padding: "0 10px",
                  boxShadow: "none",
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={textFieldStyles({ width: "25%", marginRight: "0" })}
              size="small"
            />
            <Autocomplete
              options={typeOptions}
              getOptionLabel={(option) => option.label || option}
              value={typeOptions.find(opt => opt.value === typeFilter) || null}
              onChange={(event, newValue) => setTypeFilter(newValue?.value || "")}
              sx={{ width: "25%" }}
              size="small"
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:relationships.filter_by')}
                  sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.label || option}
              value={statusOptions.find(opt => opt.value === statusFilter) || null}
              onChange={(event, newValue) => setStatusFilter(newValue?.value || "")}
              sx={{ width: "25%" }}
              size="small"
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:relationships.filter_by_status')}
                  sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            <Autocomplete
              options={contractOptions}
              getOptionLabel={(option) => option.label || option}
              value={contractOptions.find(opt => opt.value === contractFilter) || null}
              onChange={(event, newValue) => setContractFilter(newValue?.value || "")}
              sx={{ width: "25%" }}
              size="small"
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages:relationships.filter_by_contract')}
                  sx={textFieldStyles({ width: "100%", marginRight: "0" })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          </Box>
        </Grid>
      </Grid>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "32px 24px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredRelationships?.length > 0 ? (
            <TableContainer>
              <Table sx={tableStyles.container} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "uniqueNumber"}
                        direction={orderBy === "uniqueNumber" ? order : "asc"}
                        onClick={() => handleRequestSort("uniqueNumber")}
                      >
                        {t('common:id')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleRequestSort("name")}
                      >
                        {t('forms:labels.fullName')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "type"}
                        direction={orderBy === "type" ? order : "asc"}
                        onClick={() => handleRequestSort("type")}
                      >
                        {t('forms:labels.type')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleRequestSort("status")}
                      >
                        {t('forms:labels.status')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "phone"}
                        direction={orderBy === "phone" ? order : "asc"}
                        onClick={() => handleRequestSort("phone")}
                      >
                        {t('forms:labels.phone')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "email"}
                        direction={orderBy === "email" ? order : "asc"}
                        onClick={() => handleRequestSort("email")}
                      >
                        {t('forms:labels.email')}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRelationships
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((relationship) => (
                      <TableRow
                        key={relationship._id}

                        sx={{ ...tableStyles.row,}}
                      >
                        <TableCell sx={tableStyles.cell}>
                          {relationship.uniqueNumber}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            color: "#005b92",
                          }}
                          onClick={() =>
                            navigate(`/relationships/${relationship._id}`)
                          }
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              alt={relationship?.firstName || relationship?.businessName}
                              src={relationship.avatar ? relationship.avatar : ""}
                              sx={{
                                // fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontSize: "7.8px",
                                height: "1rem",
                                width: "1rem",
                                marginRight: "0.2rem",
                                fontWeight: "bold",
                                // backgroundColor: relationship.avatar
                                //   ? "transparent"
                                //   : stringToColor(relationship.firstName || relationship.businessName),
                                color: "#fff",
                                // fontWeight: relationship?.businessName ? "600" : "400"
                              }}
                            >
                              {!relationship.avatar && (
                                <>
                                  {relationship.firstName ? relationship.firstName.charAt(0) : ''}
                                  {relationship.businessName ? relationship.businessName.charAt(0) :
                                    relationship.lastName ? relationship.lastName.charAt(0) : ''}
                                </>
                              )}
                            </Avatar>
                            <Box
                              component="span" // Render as a `span` element
                              sx={{
                                "&:hover": {
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                },
                                marginLeft: "-3px"
                              }}
                            >
                              {relationship.contractType === "Individual"
                                ? `${relationship.firstName || ""} ${relationship.middleName || ""} ${relationship.lastName || ""}`
                                : relationship.businessName || ""}
                            </Box>
                          </Box>
                        </TableCell>







                        <TableCell sx={tableStyles.cell}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {relationship.contractType === "Individual" ? (
                              <>
                                <PersonOutlineIcon sx={{ fontSize: "1.2rem", color: "gray" }} />
                                {getTranslatedRelationshipType(relationship.type)}
                                {relationship.sex && (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "8.25px",
                                      fontWeight: "700",
                                      fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                      backgroundColor: "#f5f5f5",
                                      padding: "1px 6px",
                                      borderRadius: "20px",
                                      letterSpacing: "0.1em",
                                      marginLeft: "4px"
                                    }}
                                  >
                                    {translateGender(relationship.sex)}
                                  </Box>
                                )}
                              </>
                            ) : (
                              <>
                                <BusinessOutlinedIcon sx={{ fontSize: "1.2rem", color: "gray" }} />
                                {getTranslatedRelationshipType(relationship.type)}
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: "20px",
                              backgroundColor: relationship.status === "Active" ? "#85bc20" : relationship.status === "Inactive" ? "#e5202e" : "transparent",
                              color: relationship.status === "Inactive" ? "white" : "black",
                              fontSize: "8.25px",
                              fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                              fontWeight: "700",
                              textTransform: "upperCase",
                              height: "12px",
                              letterSpacing: "0.2em"
                              // display: "inline-block",
                            }}
                          >
                            {relationship.status === 'Active' ? t('common:active') : relationship.status === 'Inactive' ? t('common:inactive') : relationship.status}
                          </span>
                        </TableCell>
                        <TableCell sx={{
                          ...tableStyles.cell,
                          "&:hover": {
                            textDecoration: "underline !important",
                            cursor: "pointer"
                          },
                          color: '#005b92',


                        }}>
                          {relationship.contacts &&
                            relationship.contacts.length > 0
                            ? relationship.contacts[0].phone
                            : ""}
                        </TableCell>
                        <TableCell sx={{
                          ...tableStyles.cell,
                          "&:hover": {
                            textDecoration: "underline !important",
                            cursor: "pointer"
                          },
                          color: '#005b92',


                        }}>
                          {relationship.contacts &&
                            relationship.contacts.length > 0
                            ? relationship.contacts[0].email
                            : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={sortedRelationships?.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage={t('common:rows_per_page')}
              />
            </TableContainer>
          ) : (
            <Box
              sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
            >
              No data available
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default RelationshipPage;
